/* eslint-disable jsx-a11y/label-has-associated-control */
import { clsx } from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import { nanoid } from 'nanoid'
import { memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { NUTRIENT_COLORS, NUTRIENT_EMOJIS } from '@shared/config'
import { notyError, notySuccess } from '@shared/lib/notifications'
import { getDayJs } from '@shared/lib/utils'
import { Btn } from '@shared/ui/btn'
import { NotchLoader } from '@shared/ui/loader'

import { getMeal, updateMeal, deleteMeal } from '../api'

import styles from './styles.module.scss'

//
//

const dayjs = getDayJs()

const MEAL_TYPES = [
  'breakfast',
  'morning_snack',
  'lunch',
  'afternoon_snack',
  'dinner',
  'evening_snack',
  'unknown',
]

const MEAL_TYPE_ICONS = {
  breakfast: 'fa-mug-hot',
  morning_snack: 'fa-apple-whole',
  lunch: 'fa-bowl-food',
  afternoon_snack: 'fa-cookie-bite',
  dinner: 'fa-utensils',
  evening_snack: 'fa-moon',
  unknown: 'fa-utensils',
}

const MACRO_KEYS = ['proteins', 'fats', 'carbs']

// kcal per gram of each macro - used for the energy split bar
const ENERGY_FACTORS = {
  proteins: 4,
  fats: 9,
  carbs: 4,
}

//
//

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07 },
  },
}

const sectionVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
}

//
//

const toNumber = (value) => {
  const num = parseFloat(String(value ?? '').replace(',', '.'))

  if (!Number.isFinite(num) || num < 0) {
    return 0
  }

  return num
}

const round1 = (value) => Math.round(value * 10) / 10

// share of every macro in the meal's energy, in percent
const getEnergySplit = (macros) => {
  const energies = MACRO_KEYS.map(
    (key) => toNumber(macros?.[key]) * ENERGY_FACTORS[key]
  )

  const total = energies.reduce((sum, value) => sum + value, 0)

  if (!total) {
    return null
  }

  return Object.fromEntries(
    MACRO_KEYS.map((key, index) => [
      key,
      Math.round((energies[index] / total) * 100),
    ])
  )
}

const getItemsTotals = (items) =>
  items.reduce(
    (acc, item) => ({
      calories: round1(acc.calories + toNumber(item.calories)),
      proteins: round1(acc.proteins + toNumber(item.proteins)),
      fats: round1(acc.fats + toNumber(item.fats)),
      carbs: round1(acc.carbs + toNumber(item.carbs)),
    }),
    { calories: 0, proteins: 0, fats: 0, carbs: 0 }
  )

//
//

const FCMeal = ({ id }) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  // null = loading, { error: true } = failed, object = loaded
  const [meal, setMeal] = useState(null)

  const [isEditing, setIsEditing] = useState(false)
  const [form, setForm] = useState(null)

  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const [saveInProgress, setSaveInProgress] = useState(false)
  const [deleteInProgress, setDeleteInProgress] = useState(false)

  //
  //

  useEffect(() => {
    const fn = async () => {
      const mealData = await getMeal(id)

      if (mealData?.error) {
        notyError(t('meal:error.load'))
        setMeal({ error: true })
        return
      }

      setMeal(mealData?.data || { error: true })
    }

    fn()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  //
  //

  // stable render keys - the backend items carry no ids
  const viewItems = useMemo(
    () =>
      (meal?.mealData?.items || []).map((item) => ({
        ...item,
        key: nanoid(6),
      })),
    [meal]
  )

  const formTotals = useMemo(
    () => (form ? getItemsTotals(form.items) : null),
    [form]
  )

  //
  //

  const info = meal?.mealData?.info

  //
  //

  const startEdit = () => {
    // seed the editor with one row built from the totals when the meal has no items
    const formItems = viewItems.length
      ? viewItems.map((item) => ({
          key: item.key,
          name: item?.name || '',
          calories: String(item?.info?.calories ?? ''),
          proteins: String(item?.info?.proteins ?? ''),
          fats: String(item?.info?.fats ?? ''),
          carbs: String(item?.info?.carbs ?? ''),
        }))
      : [
          {
            key: nanoid(6),
            name: meal?.mealTitle || '',
            calories: String(info?.totalCalories ?? ''),
            proteins: String(info?.totalProteins ?? ''),
            fats: String(info?.totalFats ?? ''),
            carbs: String(info?.totalCarbs ?? ''),
          },
        ]

    setForm({
      mealTitle: meal?.mealTitle || '',
      mealDescription: meal?.mealDescription || '',
      possibleMealType: meal?.possibleMealType || 'unknown',
      items: formItems,
    })

    setConfirmingDelete(false)
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setIsEditing(false)
  }

  //
  //

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const setItemField = (itemKey, name, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.key === itemKey ? { ...item, [name]: value } : item
      ),
    }))
  }

  const addItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          key: nanoid(6),
          name: '',
          calories: '',
          proteins: '',
          fats: '',
          carbs: '',
        },
      ],
    }))
  }

  const removeItem = (itemKey) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.key !== itemKey),
    }))
  }

  //
  //

  const isFormValid =
    !!form &&
    !!form.mealTitle.trim() &&
    form.items.length > 0 &&
    form.items.every((item) => item.name.trim())

  //
  //

  const onSave = async () => {
    setSaveInProgress(true)

    const newItems = form.items.map((item) => ({
      name: item.name.trim(),
      info: {
        calories: toNumber(item.calories),
        proteins: toNumber(item.proteins),
        fats: toNumber(item.fats),
        carbs: toNumber(item.carbs),
      },
    }))

    // the backend duplicates title/description/type inside mealData.info - keep them in sync
    const newInfo = {
      ...info,
      totalCalories: formTotals.calories,
      totalProteins: formTotals.proteins,
      totalFats: formTotals.fats,
      totalCarbs: formTotals.carbs,
      mealTitle: form.mealTitle.trim(),
      mealDescription: form.mealDescription.trim(),
      possibleMealType: form.possibleMealType,
    }

    const payload = {
      mealTitle: form.mealTitle.trim(),
      mealDescription: form.mealDescription.trim(),
      possibleMealType: form.possibleMealType,
      mealData: { ...meal.mealData, info: newInfo, items: newItems },
    }

    const res = await updateMeal(id, { mealData: payload })

    setSaveInProgress(false)

    if (res?.error) {
      notyError(t('meal:error.update'))
      return
    }

    setMeal({ ...meal, ...payload, updatedAt: dayjs().toISOString() })
    setIsEditing(false)

    notySuccess(t('meal:success.update'))
  }

  //
  //

  const onDelete = async () => {
    setDeleteInProgress(true)

    const res = await deleteMeal(id)

    if (res?.error) {
      setDeleteInProgress(false)
      notyError(t('meal:error.delete'))
      return
    }

    notySuccess(t('meal:success.delete'))

    navigate('/')
  }

  //
  //

  if (meal === null) {
    return (
      <div className={styles.meal}>
        <section className={styles.card}>
          <div className="d-flex justify-content-center py-4">
            <NotchLoader size={35} />
          </div>
        </section>
      </div>
    )
  }

  if (meal.error) {
    return (
      <div className={styles.meal}>
        <section className={styles.card}>
          <p className={styles.meal__error}>{t('meal:error.load')}</p>
        </section>
      </div>
    )
  }

  //
  //

  const mealType = MEAL_TYPES.includes(meal.possibleMealType)
    ? meal.possibleMealType
    : 'unknown'

  const viewTotals = {
    calories: toNumber(info?.totalCalories),
    proteins: toNumber(info?.totalProteins),
    fats: toNumber(info?.totalFats),
    carbs: toNumber(info?.totalCarbs),
  }

  const split = getEnergySplit(viewTotals)

  const wasEdited = meal.updatedAt && meal.updatedAt !== meal.addedAt

  //
  //

  const viewContent = (
    <motion.div
      key="view"
      className={styles.meal__sections}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0, y: -6, transition: { duration: 0.15 } }}
    >
      <motion.section className={styles.card} variants={sectionVariants}>
        <div className={styles.card__header}>
          <span className={styles.type}>
            <i className={clsx('fa-solid', MEAL_TYPE_ICONS[mealType])} />
            {t(`meal:mealTypes.${mealType}`)}
          </span>

          <span className={styles.date}>
            {dayjs(meal.addedAt).format('DD.MM.YYYY, HH:mm')}
            {wasEdited && ` · ${t('meal:edited')}`}
          </span>
        </div>

        <div>
          <h1 className={styles.title}>{meal.mealTitle}</h1>

          {meal.mealDescription && (
            <p className={styles.description}>{meal.mealDescription}</p>
          )}
        </div>

        {/* ************************************************************************** */}

        <div className={styles.calories}>
          <span className={styles.calories__value}>{viewTotals.calories}</span>
          <span className={styles.calories__unit}>{t('meal:units.kcal')}</span>
        </div>

        {split && (
          <div className={styles.split}>
            {MACRO_KEYS.map((key) => (
              <motion.span
                key={key}
                style={{ backgroundColor: NUTRIENT_COLORS[key] }}
                initial={{ width: 0 }}
                animate={{ width: `${split[key]}%` }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              />
            ))}
          </div>
        )}

        <ul className={styles.macros}>
          {MACRO_KEYS.map((key) => (
            <li key={key} className={styles.macro}>
              <span className={styles.macro__name}>
                <i
                  className={styles.macro__dot}
                  style={{ backgroundColor: NUTRIENT_COLORS[key] }}
                />
                {t(`meal:nutrients.${key}`)}
              </span>

              <span className={styles.macro__value}>
                {viewTotals[key]} {t('meal:units.g')}
                {viewTotals[key] < 10000 && (
                  <span className="ms-1">{NUTRIENT_EMOJIS[key]}</span>
                )}
              </span>

              {split && (
                <span className={styles.macro__percent}>{split[key]}%</span>
              )}
            </li>
          ))}
        </ul>

        {/* ************************************************************************** */}

        {info?.nutrientsNote && (
          <div className={styles.note}>
            <i className="fa-solid fa-lightbulb" />
            <p>{info.nutrientsNote}</p>
          </div>
        )}
      </motion.section>

      {/* ************************************************************************** */}

      <motion.section className={styles.card} variants={sectionVariants}>
        <div className={styles.card__header}>
          <h2>{t('meal:items.title')}</h2>

          {viewItems.length > 0 && (
            <span className={styles.count}>{viewItems.length}</span>
          )}
        </div>

        {viewItems.length ? (
          <ul className={styles.items}>
            {viewItems.map((item) => (
              <li key={item.key} className={styles.item}>
                <div className={styles.item__row}>
                  <span className={styles.item__name}>{item.name}</span>

                  <span className={styles.item__calories}>
                    {toNumber(item?.info?.calories)} {t('meal:units.kcal')}
                  </span>
                </div>

                <span className={styles.item__macros}>
                  {MACRO_KEYS.map((key) => (
                    <span key={key}>
                      {/* <i style={{ backgroundColor: NUTRIENT_COLORS[key] }} /> */}
                      <i>{NUTRIENT_EMOJIS[key]}</i>
                      {toNumber(item?.info?.[key])} {t('meal:units.g')}
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.items__empty}>{t('meal:items.empty')}</p>
        )}
      </motion.section>

      {/* ************************************************************************** */}

      <motion.div className={styles.actions} variants={sectionVariants}>
        <Btn
          design={['primary', 'medium']}
          className={styles.actions__main}
          onClick={startEdit}
        >
          <i className="fa-solid fa-pen me-2" />
          {t('meal:buttons.edit')}
        </Btn>
      </motion.div>

      <motion.section
        className={clsx(styles.card, styles.danger)}
        variants={sectionVariants}
      >
        <AnimatePresence mode="wait" initial={false}>
          {!confirmingDelete ? (
            <motion.div
              key="trigger"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Btn
                design={['wrapper']}
                className={styles.danger__trigger}
                onClick={() => setConfirmingDelete(true)}
              >
                <i className="fa-solid fa-trash-can" />
                {t('meal:buttons.delete')}
              </Btn>
            </motion.div>
          ) : (
            <motion.div
              key="confirm"
              className={styles.danger__confirm}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <p className={styles.danger__question}>
                {t('meal:deleteConfirm.question')}
              </p>

              <p className={styles.danger__hint}>
                {t('meal:deleteConfirm.hint')}
              </p>

              <div className={styles.danger__buttons}>
                <Btn
                  design={['wrapper']}
                  className={styles.danger__cancel}
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleteInProgress}
                >
                  {t('meal:buttons.cancel')}
                </Btn>

                <Btn
                  design={['wrapper']}
                  className={styles.danger__delete}
                  onClick={onDelete}
                  isInProgress={deleteInProgress}
                >
                  {t('meal:buttons.confirmDelete')}
                </Btn>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.section>
    </motion.div>
  )

  //
  //

  const editContent = form && (
    <motion.div
      key="edit"
      className={styles.meal__sections}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0, y: -6, transition: { duration: 0.15 } }}
    >
      <motion.section className={styles.card} variants={sectionVariants}>
        <div className={styles.card__header}>
          <h2>{t('meal:editTitle')}</h2>
        </div>

        <label className={styles.field}>
          <span className={styles.field__label}>{t('meal:form.title')}</span>
          <input
            type="text"
            value={form.mealTitle}
            onChange={(e) => setField('mealTitle', e.target.value)}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.field__label}>
            {t('meal:form.description')}
          </span>
          <textarea
            rows={2}
            value={form.mealDescription}
            onChange={(e) => setField('mealDescription', e.target.value)}
          />
        </label>

        <div className={styles.field}>
          <span className={styles.field__label}>{t('meal:form.type')}</span>

          <div className={styles.types}>
            {MEAL_TYPES.map((type) => (
              <Btn
                key={type}
                design={['wrapper']}
                className={clsx(styles.chip, {
                  [styles.chip_active]: form.possibleMealType === type,
                })}
                onClick={() => setField('possibleMealType', type)}
              >
                <i className={clsx('fa-solid', MEAL_TYPE_ICONS[type])} />
                {t(`meal:mealTypes.${type}`)}
              </Btn>
            ))}
          </div>
        </div>
      </motion.section>

      {/* ************************************************************************** */}

      <motion.section className={styles.card} variants={sectionVariants}>
        <div className={styles.card__header}>
          <h2>{t('meal:items.title')}</h2>

          <span className={styles.count}>{form.items.length}</span>
        </div>

        <ul className={styles['edit-items']}>
          {form.items.map((item) => (
            <li key={item.key} className={styles['edit-item']}>
              <div className={styles['edit-item__top']}>
                <input
                  type="text"
                  value={item.name}
                  placeholder={t('meal:form.itemName')}
                  onChange={(e) =>
                    setItemField(item.key, 'name', e.target.value)
                  }
                />

                <Btn
                  design={['wrapper']}
                  className={styles['edit-item__remove']}
                  onClick={() => removeItem(item.key)}
                  disabled={form.items.length === 1}
                >
                  <i className="fa-solid fa-trash-can" />
                </Btn>
              </div>

              <div className={styles['edit-item__numbers']}>
                <label>
                  <span>{t('meal:units.kcal')}</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={item.calories}
                    onChange={(e) =>
                      setItemField(item.key, 'calories', e.target.value)
                    }
                  />
                </label>

                {MACRO_KEYS.map((key) => (
                  <label key={key}>
                    <span>
                      <i style={{ backgroundColor: NUTRIENT_COLORS[key] }} />
                      {t(`meal:macroShort.${key}`)}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={item[key]}
                      onChange={(e) =>
                        setItemField(item.key, key, e.target.value)
                      }
                    />
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ul>

        <Btn
          design={['wrapper']}
          className={styles['add-item']}
          onClick={addItem}
        >
          <i className="fa-solid fa-plus" />
          {t('meal:buttons.addItem')}
        </Btn>

        {/* ************************************************************************** */}

        <div className={styles.totals}>
          <span className={styles.totals__label}>{t('meal:form.totals')}</span>

          <span className={styles.totals__values}>
            <span>
              <b>{formTotals.calories}</b> {t('meal:units.kcal')}
            </span>

            {MACRO_KEYS.map((key) => (
              <span key={key}>
                {t(`meal:macroShort.${key}`)} <b>{formTotals[key]}</b>{' '}
                {t('meal:units.g')}
              </span>
            ))}
          </span>
        </div>
      </motion.section>

      {/* ************************************************************************** */}

      <motion.div className={styles.actions} variants={sectionVariants}>
        <Btn
          design={['primary', 'medium']}
          className={styles.actions__main}
          onClick={onSave}
          isInProgress={saveInProgress}
          disabled={!isFormValid}
        >
          {t('meal:buttons.save')}
        </Btn>

        <Btn
          design={['wrapper']}
          className={styles.actions__cancel}
          onClick={cancelEdit}
          disabled={saveInProgress}
        >
          {t('meal:buttons.cancel')}
        </Btn>
      </motion.div>
    </motion.div>
  )

  //
  //

  return (
    <div className={styles.meal}>
      <AnimatePresence mode="wait" initial={false}>
        {isEditing ? editContent : viewContent}
      </AnimatePresence>
    </div>
  )
}

export const Meal = memo(FCMeal)
